import * as Slider from '@radix-ui/react-slider';
import { type SelectOption, TextField } from '@tryhackme/thm-ui-components';
import { useState } from 'react';
import { Controller, useFormContext } from 'src/common/form';
import { StyledCategories, StyledInput, StyledSelectableTag, StyledSlider, StyledSliderLabel, StyledTagFormFields, StyledTagsSelector } from 'src/features/admin/tagging/tagging.styles';
import { type TagColour, TagColours, type TagFormData, type TagFormProps } from 'src/features/admin/tagging/tagging.types';
export const TagForm = ({
  tagCategories,
  existingTag,
  fieldsDisabled = false,
  isModal
}: TagFormProps) => {
  const {
    control,
    formState,
    getValues,
    register,
    setValue,
    watch
  } = useFormContext<TagFormData>();
  const [subcategories, setSubcategories] = useState<{
    value: string;
    label: string;
  }[]>(() => {
    const category = getValues('category');
    return category ? tagCategories.find(tagCategory => tagCategory._id === category.value)?.subcategories.map(tc => ({
      value: tc._id,
      label: tc.label
    })) ?? [] : [];
  });
  const handleCategoryDropDownChange = (field: 'category' | 'subcategory', value?: SelectOption) => {
    if (value) {
      setValue(field, value, {
        shouldValidate: true
      });
      if (field === 'category') {
        setValue('subcategory', null);
        const subcategoryValues = tagCategories.find(tagCategory => tagCategory._id === value.value)?.subcategories.map(tc => ({
          value: tc._id,
          label: tc.label
        }));
        setSubcategories(subcategoryValues || []);
      }
    }
  };
  const categoryFieldOptions = tagCategories.map(tc => ({
    value: tc._id,
    label: tc.label
  }));
  return <form data-sentry-component="TagForm" data-sentry-source-file="tag-form-fields.tsx">
      <StyledTagFormFields data-sentry-element="StyledTagFormFields" data-sentry-source-file="tag-form-fields.tsx">
        <StyledCategories $wrapFields={isModal} data-sentry-element="StyledCategories" data-sentry-source-file="tag-form-fields.tsx">
          <StyledInput data-sentry-element="StyledInput" data-sentry-source-file="tag-form-fields.tsx">
            <TextField {...register('label')} isDisabled={fieldsDisabled} variant="input" placeholder="Enter tag label..." defaultValue={existingTag?.label ?? ''} label="Label" labelPosition="left" isRequired={isModal} autoComplete="off" isError={!!formState.errors.label?.message} errorMessage={formState.errors.label?.message as string} data-sentry-element="TextField" data-sentry-source-file="tag-form-fields.tsx" />
          </StyledInput>
          <StyledInput data-sentry-element="StyledInput" data-sentry-source-file="tag-form-fields.tsx">
            <Controller name="category" control={control} render={({
            field: {
              ref,
              value,
              onChange,
              name,
              ...rest
            }
          }) => <TextField variant="select" isDisabled={fieldsDisabled} isMulti={false} isSearchable={false} options={categoryFieldOptions} selected={categoryFieldOptions.find(option => option.value === value?.value)} handleSelection={selectedValue => {
            handleCategoryDropDownChange('category', selectedValue);
          }} placeholder="Select tag category" innerRef={ref} label="Category" labelPosition="left" isRequired={isModal} {...rest} isError={!!formState.errors.category?.message} errorMessage={formState.errors.category?.message as string} />} data-sentry-element="Controller" data-sentry-source-file="tag-form-fields.tsx" />
          </StyledInput>
          <StyledInput data-sentry-element="StyledInput" data-sentry-source-file="tag-form-fields.tsx">
            <Controller name="subcategory" control={control} render={({
            field: {
              ref,
              value,
              onChange,
              name,
              ...rest
            }
          }) => <TextField variant="select" isDisabled={fieldsDisabled || subcategories.length === 0} isMulti={false} isSearchable={false} options={subcategories} selected={subcategories.find(option => option.value === value?.value)} handleSelection={selectedValue => {
            handleCategoryDropDownChange('subcategory', selectedValue);
          }} placeholder={subcategories.length === 0 ? 'No subcategories' : 'Select tag subcategory'} innerRef={ref} label="Subcategory" labelPosition="left" {...rest} />} data-sentry-element="Controller" data-sentry-source-file="tag-form-fields.tsx" />
          </StyledInput>
        </StyledCategories>
        <StyledTagsSelector data-sentry-element="StyledTagsSelector" data-sentry-source-file="tag-form-fields.tsx">
          {Object.entries(TagColours).map((value: [string, TagColour]) => <StyledSelectableTag onClick={() => setValue('colour', value)} $isSelected={watch('colour')?.[0] === value[0]} $backgroundColor={value[1].backgroundColor} $fontColor={value[1].fontColor} $borderColor={value[1].borderColor} $isDisabled={fieldsDisabled}>
              {value[0]}
            </StyledSelectableTag>)}
        </StyledTagsSelector>
        <StyledSlider data-sentry-element="StyledSlider" data-sentry-source-file="tag-form-fields.tsx">
          Weight
          <StyledSliderLabel data-sentry-element="StyledSliderLabel" data-sentry-source-file="tag-form-fields.tsx">1</StyledSliderLabel>
          <Slider.Root className="SliderRoot" defaultValue={[existingTag?.weight || 1]} max={5} min={1} step={1} disabled={fieldsDisabled} onValueChange={value => {
          if (value?.[0]) setValue('weight', value[0]);
        }} data-sentry-element="unknown" data-sentry-source-file="tag-form-fields.tsx">
            <Slider.Track className="SliderTrack" data-sentry-element="unknown" data-sentry-source-file="tag-form-fields.tsx">
              <Slider.Range className="SliderRange" data-sentry-element="unknown" data-sentry-source-file="tag-form-fields.tsx" />
            </Slider.Track>
            <Slider.Thumb className="SliderThumb" aria-label="Volume" data-sentry-element="unknown" data-sentry-source-file="tag-form-fields.tsx">
              {watch('weight')}
            </Slider.Thumb>
          </Slider.Root>
          <StyledSliderLabel data-sentry-element="StyledSliderLabel" data-sentry-source-file="tag-form-fields.tsx">5</StyledSliderLabel>
        </StyledSlider>
      </StyledTagFormFields>
    </form>;
};